import axios from "axios";
import {
  getCsrfTokenFromCookies,
  setCsrfTokenToCookies,
} from "../utils/csrf";

import store from "../state/store";
import { LOGOUT } from "../state/actions/types";
import { triggerSessionExpired } from "../utils/sessionExpired";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_VERSION = process.env.REACT_APP_API_VERSION;

export const API_BASE_URL =
  `${BASE_URL}${API_VERSION}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type":
      "application/json",
  },
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise = null;

const logoutUser = () => {
  localStorage.removeItem(
    "access_token"
  );

  localStorage.removeItem(
    "refresh_token"
  );

  store.dispatch({
    type: LOGOUT,
  });

  triggerSessionExpired();
};

const refreshCsrfToken =
  async () => {
    try {
      const res =
        await axios.get(
          `${API_BASE_URL}/auth/refresh-csrf`,
          {
            withCredentials:
              true,
          }
        );

      const csrf =
        res?.data?.data
          ?.csrf_token;

      if (!csrf)
        return null;

      setCsrfTokenToCookies(
        csrf
      );

      api.defaults.headers.common[
        "X-CSRF-Token"
      ] = csrf;

      return csrf;
    } catch {
      return null;
    }
  };

const refreshAccessToken =
  async () => {
    try {
      const refreshToken =
        localStorage.getItem(
          "refresh_token"
        );

      if (!refreshToken)
        return null;

      const res =
        await axios.post(
          `${API_BASE_URL}/auth/refresh-token`,
          {
            refresh_token:
              refreshToken,
          },
          {
            withCredentials:
              true,
          }
        );

      const newAccessToken =
        res?.data?.data
          ?.access_token;

      const newRefreshToken =
        res?.data?.data
          ?.refresh_token;

      if (!newAccessToken)
        return null;

      localStorage.setItem(
        "access_token",
        newAccessToken
      );

      if (
        newRefreshToken
      ) {
        localStorage.setItem(
          "refresh_token",
          newRefreshToken
        );
      }

      api.defaults.headers.common.Authorization =
        `Bearer ${newAccessToken}`;

      return newAccessToken;
    } catch {
      return null;
    }
  };

api.interceptors.request.use(
  config => {
    const token =
      localStorage.getItem(
        "access_token"
      );

    const csrf =
      getCsrfTokenFromCookies();

    config.headers =
      config.headers || {};

    if (token) {
      config.headers.Authorization =
        `Bearer ${token}`;
    }

    if (csrf) {
      config.headers[
        "X-CSRF-Token"
      ] = csrf;
    }

    return config;
  }
);

api.interceptors.response.use(
  response => response,

  async error => {
    const originalRequest =
      error.config;

    if (
      !error.response
    ) {
      return Promise.reject(
        error
      );
    }

    const status =
      error.response.status;

    const url =
      originalRequest?.url;

    const isRefreshEndpoint =
      url?.includes(
        "/auth/refresh-token"
      );

    const isCsrfEndpoint =
      url?.includes(
        "/auth/refresh-csrf"
      );

    /*
     * Refresh token invalid
     */
    if (
      isRefreshEndpoint
    ) {
      logoutUser();

      return Promise.reject(
        error
      );
    }

    /*
     * Prevent loops
     */
    if (
      originalRequest._retry ||
      isCsrfEndpoint
    ) {
      return Promise.reject(
        error
      );
    }

    const csrfExpired =
      status === 403 &&
      error.response.data
        ?.code ===
        "csrf_token_mismatch";

    const accessExpired =
      status === 401;

    if (
      !csrfExpired &&
      !accessExpired
    ) {
      return Promise.reject(
        error
      );
    }

    originalRequest._retry =
      true;

    try {
      if (
        !isRefreshing
      ) {
        isRefreshing =
          true;

        refreshPromise =
          (async () => {
            try {
              const newCsrf =
                await refreshCsrfToken();

              const newToken =
                await refreshAccessToken();

              return {
                newCsrf,
                newToken,
              };
            } finally {
              isRefreshing =
                false;

              refreshPromise =
                null;
            }
          })();
      }

      const {
        newToken,
        newCsrf,
      } =
        await refreshPromise;

      if (
        !newToken
      ) {
        logoutUser();

        return Promise.reject(
          error
        );
      }

      originalRequest.headers.Authorization =
        `Bearer ${newToken}`;

      if (
        newCsrf
      ) {
        originalRequest.headers[
          "X-CSRF-Token"
        ] =
          newCsrf;
      }

      return api(
        originalRequest
      );
    } catch (
      refreshError
    ) {
      logoutUser();

      return Promise.reject(
        refreshError
      );
    }
  }
);

export default api;